<script lang="ts" setup>
import { computed, ref, type Ref, watch } from "vue";
import { Frequency, RRule } from "rrule";
import DatePicker from "@/components/DateRange/DatePicker.vue";
import date from "@/libs/date";

const emit = defineEmits(["changed"]);

const frequencies = [
  { label: "Wekelijks", value: Frequency.WEEKLY },
  { label: "Maandelijks", value: Frequency.MONTHLY },
  { label: "Jaarlijks", value: Frequency.YEARLY },
];

const props = defineProps<{ initialValue: RRule | null }>();

const frequency: Ref<Frequency | null> = ref(props.initialValue?.options.freq ?? Frequency.MONTHLY);
const interval: Ref<number> = ref(props.initialValue?.options.interval ?? 1);
const count: Ref<number | null> = ref(props.initialValue?.options.count ?? null);
const until: Ref<Date | null> = ref(props.initialValue?.options.until ?? null);

const recurrence = computed(() => {
  if (frequency.value === null) {
    return null;
  }

  return new RRule({
    freq: frequency.value,
    interval: interval.value,
    count: count.value,
    until: until.value,
  });
});

const onSelectEndMode = (event: Event) => {
  const el = event.target as HTMLInputElement;

  count.value = null;
  until.value = null;

  if (el.value === "until") {
    until.value = new Date();
  } else if (el.value === "count") {
    count.value = 1;
  }
};

watch(recurrence, () => {
  emit("changed", recurrence.value);
});
</script>

<template>
  <div class="flex flex-col mt-6 p-5 bg-gray-50 rounded space-y-5 divide-y-2 divide-gray-500">
    <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 pb-5">
      <fieldset>
        <legend class="text-sm/6 font-semibold text-gray-900">Periode</legend>
        <div class="mt-6 space-y-6">
          <div v-for="(f, idx) in frequencies" :key="idx" class="flex items-center">
            <input
              :id="`freq_${f.value}`"
              v-model="frequency"
              :value="f.value"
              class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
              name="frequency"
              type="radio"
            />
            <label
              :for="`freq_${f.value}`"
              class="ml-3 block text-sm/6 font-medium text-gray-900"
              >{{ f.label }}</label
            >
          </div>
        </div>
      </fieldset>
    </div>

    <fieldset>
      <legend class="text-sm/6 font-semibold text-gray-900">Herhalingen</legend>
      <p class="mt-1 text-sm/6 text-gray-600">Hoe vaak komt de herhaling voor?</p>
      <div class="flex flex-col space-y-3">
        <div class="flex mt-6 sm:flex justify-between">
          <div class="flex space-x-1.5 items-center">
            <input
              id="end-indefinite"
              :checked="!count && !until"
              class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
              name="until"
              type="radio"
              value="inDefinite"
              @change="onSelectEndMode($event)"
            />
            <label class="ml-3 block text-sm/6 font-medium text-gray-900" for="end-indefinite"
              >Oneindig</label
            >
          </div>

          <div class="flex items-center">
            <input
              id="end-count"
              :checked="count !== null"
              class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
              name="until"
              type="radio"
              value="count"
              @change="onSelectEndMode($event)"
            />
            <label class="ml-3 block text-sm/6 font-medium text-gray-900" for="end-count"
              >Stoppen na X keer</label
            >
          </div>

          <div class="flex items-center">
            <input
              id="end-until"
              :checked="until !== null"
              class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
              name="until"
              type="radio"
              value="until"
              @change="onSelectEndMode($event)"
            />
            <label class="ml-3 block text-sm/6 font-medium text-gray-900" for="end-until"
              >Stoppen op datum</label
            >
          </div>
        </div>

        <DatePicker
          v-if="until !== null"
          :current-date="date(until)"
          @changed="until = $event"
        ></DatePicker>

        <input
          v-else-if="count !== null"
          id="count"
          v-model="count"
          class="ml-3 block min-w-0 grow py-1.5 pl-1 pr-3 text-base text-gray-900 bg-white placeholder:text-gray-400 outline outline-gray-300 rounded sm:text-sm/6"
          name="count"
          type="number"
        />
      </div>
    </fieldset>
  </div>
</template>

<style scoped></style>
